import { createAsyncThunk } from '@reduxjs/toolkit';
import { DailyInspirationFormData } from '../../screens/DailyInspiration/components/DailyInspirationForm';
import { makeFormErrorsFromResponse } from '../../components/react-hook-form/utils/make-form-errors';
import { DailyInspiration, QueryParams } from '../../types';
import { Paginated, PaginationService } from '../../services/api-handlers/pagination';
import { resolveApiCall } from '../../services/api-handlers/api-resolver';
import { DailyInspirationApi } from '../../services/end-points';
import SortingService from '../../services/sorting';
import { ThunkConfig } from '../configure-store';

export const listDailyInspirations = createAsyncThunk<Paginated<DailyInspiration>, QueryParams, ThunkConfig>(
    'daily-inspiration/list',
    async (payload, thunkAPI) => {
        const { getState } = thunkAPI;
        const { dailyInspiration } = getState();
        const { filters, sorting, ...rest } = payload;

        return PaginationService.resolveApiCall(
            thunkAPI,
            dailyInspiration,
            async () =>
                DailyInspirationApi.list({
                    ...filters,
                    ...SortingService.makeOrder(sorting),
                    ...rest,
                }),
            payload.page,
            dailyInspiration.data,
        );
    },
);

export const getAllDailyInspirations = createAsyncThunk<Paginated<DailyInspiration>, undefined, ThunkConfig>(
    'daily-inspiration/all-list',
    async (payload, thunkAPI) => {
        const { getState } = thunkAPI;
        const { dailyInspiration } = getState();

        return PaginationService.resolveApiCall(
            thunkAPI,
            dailyInspiration,
            async () =>
                DailyInspirationApi.list({
                    limit: -1,
                }),
            1,
            {
                list: [],
                page: 1,
            },
        );
    },
);

export const getDailyInspiration = createAsyncThunk<
    DailyInspiration,
    { id: string; onError: (data: any) => any },
    ThunkConfig
>('daily-inspiration/current', async (payload, thunkAPI) => {
    const { getState } = thunkAPI;
    const { dailyInspiration } = getState();
    const { id, onError } = payload;

    return resolveApiCall(thunkAPI, dailyInspiration, async () => (await DailyInspirationApi.get(id)).data, onError);
});

export const deleteDailyInspiration = createAsyncThunk<any, { id: string; callback: () => void }, ThunkConfig>(
    'daily-inspiration/delete',
    async (payload, thunkApi) => {
        const { getState } = thunkApi;
        const { id } = payload;
        const { dailyInspiration } = getState();
        await resolveApiCall(
            thunkApi,
            dailyInspiration,
            async () => (
                await DailyInspirationApi.remove(id),
                async (err: { response: any }) => {
                    const { response } = err;
                    return makeFormErrorsFromResponse(response.data);
                }
            ),
        );
        payload.callback();
    },
);

export const createDailyInspiration = createAsyncThunk<any, DailyInspirationFormData, ThunkConfig>(
    'daily-inspiration/create',
    async (payload, thunkApi) => {
        console.log('payload', payload);
        const { getState } = thunkApi;
        const { dailyInspiration } = getState();
        return resolveApiCall(
            thunkApi,
            dailyInspiration,
            async () => await DailyInspirationApi.create(payload),
            async (err) => {
                const { response } = err;
                return makeFormErrorsFromResponse(response.data);
            },
        );
    },
);

export const updateDailyInspiration = createAsyncThunk<
    DailyInspiration,
    { id: DailyInspiration['id']; data: Partial<DailyInspirationFormData> },
    ThunkConfig
>('daily-inspiration/update', async (payload, thunkApi) => {
    const { getState } = thunkApi;
    const { dailyInspiration } = getState();
    const { id, data } = payload;

    return resolveApiCall(
        thunkApi,
        dailyInspiration,
        async () => await DailyInspirationApi.update(id, data),
        async (err) => {
            const { response } = err;
            return makeFormErrorsFromResponse(response.data);
        },
    );
});
